import type { WebsiteLayoutFamily, WebsitePageKind } from '../../types/website-templates';
import { CATEGORY_CATALOG_SORT_KEYS } from '../category-catalog';
import { STORE_OWNED_ROUTE_PAGE_KIND } from './page-kinds';

/**
 * C9 canvas store routes. The admin-embedded canvas runs in root address mode
 * (D-P0-15): every path it holds is `/s/<slug>/…` of ONE store. Both sides of
 * the bridge use this parser, so a path that names another store, another
 * origin or a route outside the store can never be navigated to or reported.
 */

/** Canvas-only: page kind `order`. There is no order to show in the editor. */
export const WEBSITE_CANVAS_ORDER_PATTERN = '/s/:slug/order-confirmation';

export const WEBSITE_CANVAS_PATH_MAX_LENGTH = 512;
/** Code points. The catalog itself reads fewer; longer is refused, not cut. */
export const WEBSITE_CANVAS_SEARCH_MAX_LENGTH = 100;

export interface WebsiteCanvasStorePath {
  /** Canonical, `/s/<slug>/…` (+ optional `?q=&sort=` on catalog routes). */
  readonly path: string;
  readonly pageKind: WebsitePageKind;
  /** Store-relative, e.g. `/p/<id>`, `/`: what the Admin address bar shows. */
  readonly displayPath: string;
}

const STORE_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const STORE_SLUG_MAX_LENGTH = 63;
/** One route parameter: ids, slugs and collection refs. Never `.`, `%`, `:` or `/`. */
const PARAM_SOURCE = '[A-Za-z0-9][A-Za-z0-9_-]{0,127}';
const SLUG_FORM_PREFIX = '/s/:slug';
const QUERY_KEYS = ['q', 'sort'] as const;

interface RouteMatcher {
  /** Matches the part of the path after `/s/<slug>`. */
  readonly pattern: RegExp;
  readonly pageKind: WebsitePageKind;
}

const MATCHERS: readonly RouteMatcher[] = [
  ...Object.entries(STORE_OWNED_ROUTE_PAGE_KIND).filter(
    ([pattern]) => pattern === SLUG_FORM_PREFIX || pattern.startsWith(`${SLUG_FORM_PREFIX}/`),
  ),
  [WEBSITE_CANVAS_ORDER_PATTERN, 'order'] as const,
].map(([pattern, pageKind]) => ({
  pattern: new RegExp(
    `^${pattern
      .slice(SLUG_FORM_PREFIX.length)
      .split('/')
      .map((segment) => (segment.startsWith(':') ? PARAM_SOURCE : segment))
      .join('/')}$`,
  ),
  pageKind,
}));

function isStoreSlug(value: unknown): value is string {
  return (
    typeof value === 'string' && value.length <= STORE_SLUG_MAX_LENGTH && STORE_SLUG_RE.test(value)
  );
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x20 || code === 0x7f) return true;
  }
  return false;
}

function matchPageKind(rest: string): WebsitePageKind | null {
  return MATCHERS.find((matcher) => matcher.pattern.test(rest))?.pageKind ?? null;
}

/** Canonical query (`q` then `sort`), '' for none, null when it is not allowed. */
function canonicalQuery(rawQuery: string, pageKind: WebsitePageKind): string | null {
  if (rawQuery === '') return '';
  if (pageKind !== 'catalog') return null;

  const params = new URLSearchParams(rawQuery);
  const seen = new Set<string>();
  for (const key of params.keys()) {
    if (!(QUERY_KEYS as readonly string[]).includes(key) || seen.has(key)) return null;
    seen.add(key);
  }

  const canonical = new URLSearchParams();
  const search = params.get('q');
  if (search !== null && search !== '') {
    if ([...search].length > WEBSITE_CANVAS_SEARCH_MAX_LENGTH) return null;
    for (const char of search) {
      const code = char.codePointAt(0) ?? 0;
      if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) return null;
    }
    canonical.set('q', search);
  }
  const sort = params.get('sort');
  if (sort !== null) {
    if (!(CATEGORY_CATALOG_SORT_KEYS as readonly string[]).includes(sort)) return null;
    if (sort !== 'default') canonical.set('sort', sort);
  }
  const query = canonical.toString();
  return query === '' ? '' : `?${query}`;
}

/**
 * Accepts only this store's slug-form store-owned routes and the canvas order
 * route. Refuses other slugs, tenant-form paths, `//`, `..`, `\`, schemes,
 * fragments, encoded characters, whitespace, paths over 512 characters,
 * unknown or repeated query keys and a search over 100 code points.
 */
export function parseWebsiteCanvasStorePath(
  raw: unknown,
  storeSlug: string,
): WebsiteCanvasStorePath | null {
  if (!isStoreSlug(storeSlug)) return null;
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > WEBSITE_CANVAS_PATH_MAX_LENGTH) {
    return null;
  }
  if (raw.includes('#') || raw.includes('\\') || hasControlCharacter(raw)) return null;

  const queryStart = raw.indexOf('?');
  const pathname = queryStart === -1 ? raw : raw.slice(0, queryStart);
  const rawQuery = queryStart === -1 ? '' : raw.slice(queryStart + 1);

  const home = `/s/${storeSlug}`;
  if (pathname !== home && !pathname.startsWith(`${home}/`)) return null;
  if (pathname.includes('//') || pathname.includes('..')) return null;

  // One trailing slash is the same route; the canonical form has none.
  const rest = pathname.slice(home.length).replace(/\/$/, '');
  const pageKind = matchPageKind(rest);
  if (!pageKind) return null;

  const query = canonicalQuery(rawQuery, pageKind);
  if (query === null) return null;

  return {
    path: `${home}${rest}${query}`,
    pageKind,
    displayPath: `${rest === '' ? '/' : rest}${query}`,
  };
}

/** `/s/<slug>`: where a new canvas session starts. */
export function websiteCanvasHomePath(storeSlug: string): string {
  return `/s/${encodeURIComponent(storeSlug)}`;
}

/**
 * The route the canvas opens for a page kind. `product` has no route of its
 * own: the canvas picks a real product (C8 `host.navigate` target `page`).
 */
export function websiteCanvasPathForPageKind(
  pageKind: WebsitePageKind,
  storeSlug: string,
  layoutFamily?: WebsiteLayoutFamily,
): string | null {
  const home = websiteCanvasHomePath(storeSlug);
  switch (pageKind) {
    case 'home':
      return home;
    case 'catalog':
      return `${home}/${layoutFamily === 'beauty' ? 'shop' : 'categories'}`;
    case 'product':
      return null;
    case 'cart':
      return `${home}/cart`;
    case 'checkout':
      return `${home}/checkout`;
    case 'order':
      return `${home}/order-confirmation`;
    case 'store-info':
      return `${home}/about`;
  }
}
